import React from 'react';
import type { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { JoyKitThemeProvider, ToastContainer, createGlobalStyle } from '@withjoy/joykit';
import type { SegmentService, Logger, LoginManager } from '@shared/core';
import { AnalyticsProvider, CoreConfigProvider } from '@shared/core';
import { AuthProvider } from '@shared/components/AuthProvider';
import { FilestackProvider } from '@shared/utils/filestack';
import type { ConfigReturn } from '@static/js/env.config';
import type { ApolloClient } from '@apollo/client';
import { LocaleProvider } from '@localization/context/LocaleContext';
import { FeatureFlagsProvider } from '@shared/core/featureFlags';
import { IntercomProvider, intercomService } from '@shared/core/intercom';
import { HelmetProvider } from 'react-helmet-async';
import type { MediaScreen } from '@shared/utils/media/DefaultMediaMatchProvider';
import { DefaultMediaMatchProvider } from '@shared/utils/media/DefaultMediaMatchProvider';
import { CurrencyProvider } from '@shared/utils/currency';
import { WrappedApolloProvider } from '@shared/core/apollo/WrappedApolloProvider';
import { MfaProvider } from '@shared/components/MfaController/MfaControllerProvider';
import { VimeoPlayerProvider } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import { RouteBasedServices } from '@shared/core/RouteBasedServices';
import { EventInfoProvider } from '@shared/utils/eventInfo';
import { EventGroupSender } from '@shared/core/analytics/EventGroupSender';
import { UserConsentProvider } from '@shared/utils/userConsent';
import { FullPageLoaderRenderer } from '@shared/components/JoyLogoLoader/FullPageLoaderProvider';
import type { Variants } from '@amplitude/experiment-js-client';
import type { EventWebsiteState } from '@graphql/generated-base';
import { ThemeStylesProvider } from '@shared/utils/styleApplicator/ThemeStylesProvider';
import { GalleryPlacementPeriodProvider } from '@shared/utils/galleryPlacementPeriod/GalleryPlacementPeriodProvider';
import type { ThemeArtRecorder } from '@shared/utils/styleApplicator/themeArtRecorder.types';

interface SetupProps
  extends Readonly<{
    logger: Logger;
    segment: SegmentService;
    apolloClient: ApolloClient<NormalizedCacheObject>;
    loginManager: LoginManager;
    config: ConfigReturn;
    helmetContext?: object;
    defaultScreen?: MediaScreen;
    initialEventHandleForEventInfoProvider?: string;
    initialEventId?: string;
    initialAmplitudeFeatureFlagVariants?: Variants;
    /** Server only: the gallery placement period this request rendered with (PRNT-5494). */
    initialGalleryPlacementPeriod?: number;
    /** Server-only: resolves theme art during SSR. The client reads the inlined seed instead. */
    themeArtRecorder?: ThemeArtRecorder;
    initialEventFeatureFlags?: ReadonlyArray<string>;
    initialEventWebsiteState?: EventWebsiteState;
    initialEventType?: string;
  }> {}

const GlobalStyle = createGlobalStyle`
  :root {
    --full-screen-height: calc(100vh - (100vh - 100%));
  }
  #root {
    /**
    100vh units does not play well on mobile web - chrome and safari collapse the address bar and optional tab bar at the top, and the
    tool bar at the bottom.

    "When the address bar is visible, the bottom of the screen gets cut off since mobile browsers incorrectly set 100vh to be the
    height of the screen without the address bar showing. In the image above, the button which should be at the bottom of the screen
    is instead hidden. Worse, when a user first goes to a website on mobile the address bar will be visible at the top, so the broken
    experience is the default experience."

    Solution: Use the css property "fill-available" for browsers that support it, and default to 100vh.

    References:
      - https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html
      - https://bugs.chromium.org/p/chromium/issues/detail?id=844848#c4
      - https://gist.github.com/claus/622a938d21d80f367251dc2eaaa1b2a9
      - https://caniuse.com/#search=fill-available
    */
    min-height: var(--full-screen-height);
    min-height: -webkit-fill-available;
    display: flex;
    flex-direction: column;
    > *:first-child {
      flex: 1;
    }
  }

  body.overlay-open {
    overflow: hidden;
    padding-right: 15px;
  }

  *,::after,::before {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

export const Setup: React.FC<SetupProps> = ({
  defaultScreen,
  loginManager,
  segment,
  apolloClient,
  config,
  initialEventHandleForEventInfoProvider,
  initialEventId,
  initialEventFeatureFlags,
  initialEventWebsiteState,
  initialEventType,
  initialAmplitudeFeatureFlagVariants,
  initialGalleryPlacementPeriod,
  themeArtRecorder,
  helmetContext = {},
  children,
}) => {
  return (
    <DefaultMediaMatchProvider defaultScreen={defaultScreen}>
      <IntercomProvider intercom={intercomService}>
        <LocaleProvider>
          <CurrencyProvider>
            <WrappedApolloProvider client={apolloClient}>
              <AnalyticsProvider segment={segment}>
                <AuthProvider loginManager={loginManager}>
                  <EventInfoProvider
                    eventHandle={initialEventHandleForEventInfoProvider}
                    initialEventId={initialEventId}
                    initialEventFeatureFlags={initialEventFeatureFlags}
                    initialEventWebsiteState={initialEventWebsiteState}
                    initialEventType={initialEventType}
                  >
                    <EventGroupSender />
                    <UserConsentProvider>
                      <FeatureFlagsProvider initialAmplitudeFeatureFlagVariants={initialAmplitudeFeatureFlagVariants}>
                        <ThemeStylesProvider themeArtRecorder={themeArtRecorder}>
                          <FullPageLoaderRenderer />
                          <MfaProvider>
                            <JoyKitThemeProvider>
                              <CoreConfigProvider {...config}>
                                <HelmetProvider context={helmetContext}>
                                  <FilestackProvider>
                                    <VimeoPlayerProvider>
                                      <ToastContainer />
                                      <>
                                        <RouteBasedServices />
                                        <GlobalStyle />
                                        <GalleryPlacementPeriodProvider initialGalleryPlacementPeriod={initialGalleryPlacementPeriod}>{children}</GalleryPlacementPeriodProvider>
                                      </>
                                    </VimeoPlayerProvider>
                                  </FilestackProvider>
                                </HelmetProvider>
                              </CoreConfigProvider>
                            </JoyKitThemeProvider>
                          </MfaProvider>
                        </ThemeStylesProvider>
                      </FeatureFlagsProvider>
                    </UserConsentProvider>
                  </EventInfoProvider>
                </AuthProvider>
              </AnalyticsProvider>
            </WrappedApolloProvider>
          </CurrencyProvider>
        </LocaleProvider>
      </IntercomProvider>
    </DefaultMediaMatchProvider>
  );
};
